import { useEffect, useRef, useState } from "react";
import { BridgeApi, AuthApi } from "../lib/api";
import { useToast } from "../lib/toast";
import { Button, Pill } from "../components/primitives";
import { Check, AlertCircle, X as XIcon, Upload, Copy } from "lucide-react";
import { cn } from "../lib/cn";
import type { ChannelPlatform, InboundACL, Route } from "../lib/types";
import { RouteMcpPicker } from "./Routes";

// Per-platform add wizards. Each wizard collects platform-specific creds,
// validates them via /api/bridge/channels/validate, and only then calls
// createChannel. Editing existing channels still uses the generic JSON
// editor in Channels.tsx — the wizards are only for first-time setup.

interface WizardCommonProps {
  onCreated: () => void;
  onCancel: () => void;
  personaNames: string[];
  /** All routes — needed to populate the assist "report to" dropdown in the
   *  channel-defaults section. */
  routes: Route[];
  /** Persona → granted MCP servers, for the channel-defaults MCP picker. */
  mcpByPersona: Record<string, string[]>;
}

// ── Channel-level routing defaults — shared by the editor AND every wizard so
//    the new-channel forms never drift from the edit form. Controlled. ──────

export interface ChannelDefaults {
  default_persona: string;
  default_engagement: "" | "all" | "mention" | "observe";
  default_assist_report_to: string;
  auto_route: boolean;
  default_mcp_servers: string[] | null;   // MCP allowlist auto-routed observers inherit; null = all granted
  default_instructions: string;            // extra instructions auto-routed observers inherit into routes.instructions
  default_acl: InboundACL | "";
}

export const EMPTY_CHANNEL_DEFAULTS: ChannelDefaults = {
  default_persona: "",
  default_engagement: "",
  default_assist_report_to: "",
  auto_route: false,
  default_mcp_servers: null,
  default_instructions: "",
  default_acl: "",
};

function DefField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-fg-subtle mt-0.5">{hint}</span>}
    </label>
  );
}

export function ChannelDefaultsFields({
  value, onChange, personaNames, routes, mcpByPersona,
}: {
  value: ChannelDefaults;
  onChange: (patch: Partial<ChannelDefaults>) => void;
  personaNames: string[];
  routes: Route[];
  mcpByPersona: Record<string, string[]>;
}) {
  const assistTargets = routes.filter((r) => r.persona === value.default_persona && !r.behavior?.observe);
  const cur = value.default_assist_report_to;
  // A stored target that isn't a valid option (route renamed/deleted) would
  // otherwise render as a silent "none" — flag it so it's obviously broken.
  const assistDangling = !!cur && !assistTargets.some((r) => r.name === cur);
  const assistNotFound = !!cur && !routes.some((r) => r.name === cur);
  return (
    <div className="space-y-2.5">
      <DefField label="Default persona">
        <select value={value.default_persona} onChange={(e) => onChange({ default_persona: e.target.value })} className="w-full">
          <option value="">— none —</option>
          {personaNames.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </DefField>
      <DefField label="Default engagement">
        <select
          value={value.default_engagement}
          onChange={(e) => onChange({ default_engagement: e.target.value as ChannelDefaults["default_engagement"] })}
          className="w-full"
        >
          <option value="">— none —</option>
          <option value="all">all — reply on every batch</option>
          <option value="mention">mention — lurk; reply on mention/reply/recent</option>
          <option value="observe">observe — silent; gather memory, never reply</option>
        </select>
      </DefField>
      <DefField
        label="Auto-route new chats"
        hint="When on, each NEW chat on this channel is auto-created as a route with the persona + engagement above — no Requests step. v1 supports OBSERVE only (silent + memory, safe to auto-apply); other engagements still go to Requests for manual confirm. Off = the Requests form is just pre-filled."
      >
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={value.auto_route}
            onChange={(e) => onChange({ auto_route: e.target.checked })}
            disabled={!value.default_persona || value.default_engagement !== "observe"}
            className="size-3.5"
          />
          <span className="text-xs text-fg-muted">Auto-create observer routes (needs persona + engagement=observe)</span>
        </label>
        {value.auto_route && value.default_engagement === "observe" && (
          <span className="block text-[11px] text-warning mt-1">
            Every new chat here becomes a silent {value.default_persona || "persona"} observer. Use only on controlled channels (e.g. Asana/Email) — never on open inbound.
          </span>
        )}
      </DefField>
      {value.default_engagement === "observe" && (
        <DefField label="Assist — report to (optional)" hint="A chat (one of this persona's routes, e.g. a DM) for auto-observers to propose help into; on your “yes” they act on the observed chat. Blank = pure silent memory.">
          <select
            value={cur}
            onChange={(e) => onChange({ default_assist_report_to: e.target.value })}
            className={cn("w-full", assistDangling && "ring-1 ring-warning")}
            disabled={!value.default_persona}
          >
            <option value="">— none (silent memory only) —</option>
            {assistTargets.map((r) => <option key={r.name} value={r.name}>{r.name}{r.chat_name ? ` — ${r.chat_name}` : ""}</option>)}
            {assistDangling && <option value={cur}>{cur} — ⚠ {assistNotFound ? "route not found" : "invalid target"}</option>}
          </select>
          {assistDangling && (
            <span className="block text-[11px] text-warning mt-1">⚠ <span className="font-mono">{cur}</span> {assistNotFound ? "is not an existing route" : "isn’t a valid target for this persona"} — auto-observers won’t report anywhere. Pick a real route (or none).</span>
          )}
          {!value.default_persona && <span className="block text-[11px] text-fg-subtle mt-1">Pick a default persona first.</span>}
          {value.default_persona && !assistDangling && assistTargets.length === 0 && (
            <span className="block text-[11px] text-fg-subtle mt-1">{value.default_persona} has no non-observer route to report into — create a DM route for them first.</span>
          )}
        </DefField>
      )}
      {value.default_persona && value.default_engagement === "observe" && (
        <DefField label="MCP servers for auto-routes" hint="Which of the persona's granted MCP servers the auto-created observer routes inherit. All-checked = all grants (default). Only applies when Auto-route is on.">
          <RouteMcpPicker
            available={mcpByPersona[value.default_persona] ?? []}
            persona={value.default_persona}
            selected={value.default_mcp_servers}
            onChange={(default_mcp_servers) => onChange({ default_mcp_servers })}
            scope="channel"
          />
        </DefField>
      )}
      {value.default_persona && value.default_engagement === "observe" && (
        <DefField label="Extra instructions for auto-routes" hint="Standing instructions the auto-created observer routes inherit (injected into the persona prompt). Only applies when Auto-route is on. Per-route instructions can still be edited on the Routes page.">
          <textarea
            value={value.default_instructions}
            onChange={(e) => onChange({ default_instructions: e.target.value })}
            rows={3}
            className="w-full font-sans text-sm"
            placeholder="e.g. 'These are AYANA guest-support groups; watch for reservation/ops-dashboard issues and escalate via alfred.'"
          />
        </DefField>
      )}
      <DefField label="Default inbound ACL">
        <select value={value.default_acl} onChange={(e) => onChange({ default_acl: e.target.value as InboundACL | "" })} className="w-full">
          <option value="">— inherit global default —</option>
          <option value="allow">allow</option>
          <option value="deny">deny</option>
        </select>
      </DefField>
    </div>
  );
}

interface WizardShellProps extends WizardCommonProps {
  platform: ChannelPlatform;
  title: string;
  intro: string;
  children: React.ReactNode;
  canValidate: boolean;
  credentials: Record<string, unknown>;
  defaultName: string;
}

function WizardShell({
  platform,
  title,
  intro,
  children,
  canValidate,
  credentials,
  defaultName,
  personaNames,
  routes,
  mcpByPersona,
  onCreated,
  onCancel,
}: WizardShellProps) {
  const toast = useToast();
  const [name, setName] = useState(defaultName);
  const [defaults, setDefaults] = useState<ChannelDefaults>(EMPTY_CHANNEL_DEFAULTS);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<
    { ok: boolean; identifier?: string; details?: Record<string, unknown>; hints?: Record<string, unknown>; error?: string }
    | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [createdChannel, setCreatedChannel] = useState<{ id: number; platform: string } | null>(null);

  async function validate() {
    setValidating(true);
    setValidation(null);
    try {
      const res = await BridgeApi.validateChannel({ platform, credentials });
      setValidation(res);
    } catch (err) {
      setValidation({ ok: false, error: String(err) });
    } finally {
      setValidating(false);
    }
  }

  const [phase, setPhase] = useState<"editing" | "creating" | "restarting" | "done">("editing");

  async function save() {
    if (!validation?.ok) return;
    if (!/^[a-z0-9_-]+$/.test(name)) {
      toast.error("Invalid name", "Use lowercase letters, digits, _, -");
      return;
    }
    setSaving(true);
    try {
      setPhase("creating");
      const created = await BridgeApi.createChannel({
        name,
        platform,
        identifier: validation.identifier ?? null,
        credentials,
        default_persona: defaults.default_persona || null,
        default_engagement: defaults.default_engagement || null,
        default_assist_report_to: defaults.default_assist_report_to || null,
        auto_route: defaults.auto_route,
        default_mcp_servers: defaults.default_mcp_servers,
        default_instructions: defaults.default_instructions || null,
        default_acl: defaults.default_acl || null,
        status: "connected",
        enabled: true,
      });
      setCreatedChannel(created as { id: number; platform: string });
      // Auto-restart the bridge so the new channel starts ingesting without
      // the operator having to ssh in and run systemctl.
      setPhase("restarting");
      try { await BridgeApi.restartAndWait(); }
      catch (err) { toast.error("Bridge restart timed out", String(err)); }
      setPhase("done");
      // Don't call onCreated() right away for gchat — let the operator
      // copy the webhook URL first. They'll hit "Close" themselves.
      if (platform !== "gchat" && platform !== "asana") onCreated();
    } catch (err) {
      toast.error("Create failed", String(err));
      setPhase("editing");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">{title}</h2>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          <XIcon className="size-3.5" />
          Cancel
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 text-sm">
        <p className="text-xs text-fg-muted">{intro}</p>

        {/* Platform-specific credential form */}
        <div className="space-y-3">{children}</div>

        {/* Validate */}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={validate} disabled={!canValidate} loading={validating}>
            Validate credentials
          </Button>
          {validation && (
            validation.ok ? (
              <Pill tone="success">
                <Check className="size-3 mr-0.5" />
                {validation.identifier ? `→ ${validation.identifier}` : "OK"}
              </Pill>
            ) : (
              <Pill tone="danger">
                <AlertCircle className="size-3 mr-0.5" />
                {validation.error ?? "Invalid"}
              </Pill>
            )
          )}
        </div>

        {validation?.hints && (
          <div className="panel-elevated p-3 text-xs space-y-1">
            {Object.entries(validation.hints).map(([k, v]) => (
              <div key={k}>
                <span className="text-fg-subtle">{k}:</span>{" "}
                <code className="text-fg break-all">{String(v)}</code>
              </div>
            ))}
          </div>
        )}

        {/* Name + defaults (always shown, gated on a passing validation) */}
        {validation?.ok && (
          <div className="space-y-3 pt-2 border-t border-border">
            <label className="block">
              <span className="block text-xs text-fg-muted mb-1">Channel name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. whatsapp-personal"
                className="w-full"
              />
              <span className="block text-[11px] text-fg-subtle mt-0.5">Lowercase letters, digits, _, -. Must be unique.</span>
            </label>

            <ChannelDefaultsFields
              value={defaults}
              onChange={(p) => setDefaults((d) => ({ ...d, ...p }))}
              personaNames={personaNames}
              routes={routes}
              mcpByPersona={mcpByPersona}
            />

            <Button onClick={save} loading={saving} disabled={!name || phase !== "editing"}>
              Create channel
            </Button>
            {phase === "creating" && (
              <p className="text-[11px] text-fg-muted">Creating channel…</p>
            )}
            {phase === "restarting" && (
              <p className="text-[11px] text-fg-muted">
                Restarting bridge to start ingest from this channel…
              </p>
            )}
            {phase === "done" && (
              <div className="space-y-3">
                <p className="text-[11px] text-success">Bridge restarted — channel is live.</p>
                {createdChannel && (createdChannel.platform === "gchat" || createdChannel.platform === "asana") && (
                  <NewChannelWebhookCallout channel={createdChannel} onClose={onCreated} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Telegram ─────────────────────────────────────────────────────────────

export function TelegramWizard(props: WizardCommonProps) {
  const [token, setToken] = useState("");
  return (
    <WizardShell
      platform="telegram"
      title="Add Telegram bot"
      intro="Create a bot via @BotFather, copy the token, and paste it below. The bridge validates it via getMe before saving."
      canValidate={token.length > 0}
      credentials={{ bot_token: token }}
      defaultName="telegram-1"
      {...props}
    >
      <label className="block">
        <span className="block text-xs text-fg-muted mb-1">Bot token</span>
        <input
          value={token}
          onChange={(e) => setToken(e.target.value.trim())}
          placeholder="123456789:ABC..."
          className="w-full font-mono text-xs"
          spellCheck={false}
        />
      </label>
    </WizardShell>
  );
}

// ─── Google Chat ──────────────────────────────────────────────────────────

export function GchatWizard(props: WizardCommonProps) {
  const toast = useToast();
  const [jsonText, setJsonText] = useState("");
  const [parsed, setParsed] = useState<Record<string, unknown> | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  function handleText(text: string) {
    setJsonText(text);
    setParseError(null);
    setParsed(null);
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const obj = JSON.parse(trimmed);
      if (typeof obj === "object" && obj !== null) setParsed(obj as Record<string, unknown>);
      else setParseError("Service account key must be a JSON object");
    } catch (err) {
      setParseError(`Not valid JSON: ${String(err).slice(0, 80)}`);
    }
  }

  async function handleFile(file: File) {
    try {
      const text = await file.text();
      handleText(text);
    } catch (err) {
      toast.error("File read failed", String(err));
    }
  }

  return (
    <WizardShell
      platform="gchat"
      title="Add Google Chat (service account)"
      intro="Create a service account in Google Cloud Console, give it the Chat API scope, download the JSON key, and paste / upload it below."
      canValidate={parsed !== null}
      credentials={{ service_account_key: parsed }}
      defaultName="gchat-1"
      {...props}
    >
      <label className="block">
        <span className="block text-xs text-fg-muted mb-1">Service account JSON</span>
        <textarea
          value={jsonText}
          onChange={(e) => handleText(e.target.value)}
          placeholder='{"type":"service_account","project_id":"...","client_email":"..."}'
          className="w-full font-mono text-xs min-h-[140px]"
          spellCheck={false}
        />
        {parseError && <span className="block text-[11px] text-danger mt-0.5">{parseError}</span>}
        {parsed && typeof parsed.client_email === "string" && (
          <span className="block text-[11px] text-success mt-0.5">
            Parsed — client_email: <code>{String(parsed.client_email)}</code>
          </span>
        )}
      </label>
      <label className="inline-flex items-center gap-1 text-xs text-fg-muted cursor-pointer">
        <Upload className="size-3.5" />
        <span>Or upload the JSON file</span>
        <input
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
      </label>
    </WizardShell>
  );
}

// ─── WhatsApp ─────────────────────────────────────────────────────────────
//
// WhatsApp is its own flow — it doesn't fit the WizardShell pattern because
// validation IS the auth handshake. We render a name input, then on submit
// open an SSE stream to /api/bridge/channels/wa-auth, render wacli's ASCII QR
// in monospace, and only POST /api/bridge/channels once wacli reports a
// successful link.

export function WhatsAppWizard({ onCreated, onCancel, personaNames, routes, mcpByPersona }: WizardCommonProps) {
  const toast = useToast();
  const [name, setName] = useState("whatsapp-1");
  const [phase, setPhase] = useState<"name" | "scanning" | "restarting" | "linked" | "error">("name");
  const [qrLines, setQrLines] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<ChannelDefaults>(EMPTY_CHANNEL_DEFAULTS);
  const esRef = useRef<EventSource | null>(null);

  function reset() {
    esRef.current?.close();
    esRef.current = null;
    setPhase("name");
    setQrLines([]);
    setErrorMsg(null);
  }

  async function startScan() {
    if (!/^[a-z0-9_-]+$/.test(name)) {
      toast.error("Invalid name", "lowercase letters, digits, _, -");
      return;
    }
    setPhase("scanning");
    setQrLines([]);

    // 1. Create the channel row up front with status="pending". This gives
    //    us the integer id (→ the stable slug "wa-<id>") that the wacli
    //    session dir is keyed by. Doing this before the QR scan means the
    //    auth process writes to the slug-based path from the start —
    //    nothing to rename later if the user changes the channel's `name`.
    let channelId: number;
    try {
      const ch = await BridgeApi.createChannel({
        name,
        platform: "whatsapp",
        status: "pending",
        enabled: true,
        default_persona: defaults.default_persona || null,
        default_engagement: defaults.default_engagement || null,
        default_assist_report_to: defaults.default_assist_report_to || null,
        auto_route: defaults.auto_route,
        default_mcp_servers: defaults.default_mcp_servers,
        default_instructions: defaults.default_instructions || null,
        default_acl: defaults.default_acl || null,
      });
      channelId = (ch as { id: number }).id;
    } catch (err) {
      setErrorMsg(String(err));
      setPhase("error");
      return;
    }

    const es = new EventSource(`/api/bridge/channels/wa-auth?channel_id=${channelId}`);
    esRef.current = es;
    es.addEventListener("qr", (e) => {
      try {
        const { line } = JSON.parse((e as MessageEvent).data) as { line: string };
        setQrLines((prev) => [...prev, line]);
      } catch { /* */ }
    });
    es.addEventListener("status", () => { /* metadata only */ });
    es.addEventListener("linked", async () => {
      es.close();
      esRef.current = null;
      try {
        await BridgeApi.updateChannel(channelId, { status: "connected" });
        setPhase("restarting");
        try { await BridgeApi.restartAndWait(); }
        catch (err) { console.warn("restart timed out", err); }
        setPhase("linked");
        onCreated();
      } catch (err) {
        setErrorMsg(String(err));
        setPhase("error");
      }
    });
    es.addEventListener("error", (e) => {
      try {
        const { exitCode } = JSON.parse((e as MessageEvent).data ?? "{}") as { exitCode?: number };
        setErrorMsg(`wacli auth exited (code=${exitCode})`);
      } catch {
        setErrorMsg("connection lost");
      }
      setPhase("error");
      es.close();
      esRef.current = null;
    });
  }

  function cancel() {
    esRef.current?.close();
    esRef.current = null;
    onCancel();
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">Add WhatsApp channel</h2>
        <Button size="sm" variant="secondary" onClick={cancel}>
          <XIcon className="size-3.5" />
          Cancel
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {phase === "name" && (
          <>
            <p className="text-xs text-fg-muted">
              Choose a name for this channel. The session lives at
              {" "}<code>~/.agent-fleet/wacli-sessions/{name || "<name>"}/</code>{" "}
              and is system-managed.
            </p>
            <label className="block">
              <span className="block text-xs text-fg-muted mb-1">Channel name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full font-mono text-xs"
                placeholder="whatsapp-personal"
              />
            </label>
            <ChannelDefaultsFields
              value={defaults}
              onChange={(p) => setDefaults((d) => ({ ...d, ...p }))}
              personaNames={personaNames}
              routes={routes}
              mcpByPersona={mcpByPersona}
            />
            <Button onClick={startScan} disabled={!name}>
              Start QR scan
            </Button>
            <p className="text-[11px] text-fg-subtle">
              When the QR appears below, scan it from WhatsApp → Settings → Linked Devices.
            </p>
          </>
        )}

        {phase === "scanning" && (
          <>
            <p className="text-xs text-fg-muted">
              Scan this QR code with WhatsApp on your phone: <strong>Settings → Linked Devices → Link a device</strong>.
              The code refreshes every ~20 seconds.
            </p>
            <pre className="font-mono text-[8px] leading-none whitespace-pre overflow-auto panel-elevated p-2 max-h-[500px]">
              {qrLines.length === 0 ? "starting…" : qrLines.join("\n")}
            </pre>
            <Button variant="secondary" onClick={reset}>
              <XIcon className="size-3.5" />
              Stop
            </Button>
          </>
        )}

        {phase === "restarting" && (
          <p className="text-xs text-fg-muted">
            Linked — restarting bridge to start ingest from this channel…
          </p>
        )}

        {phase === "linked" && (
          <div className="space-y-3">
            <p className="text-xs text-success flex items-center gap-1">
              <Check className="size-3.5" />
              Linked — channel is live.
            </p>
          </div>
        )}

        {phase === "error" && (
          <div className="space-y-3">
            <p className="text-xs text-danger flex items-center gap-1">
              <AlertCircle className="size-3.5" />
              {errorMsg ?? "Authentication failed."}
            </p>
            <Button variant="secondary" onClick={reset}>Try again</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Asana ────────────────────────────────────────────────────────────────

// Asana doesn't reuse WizardShell because creating the channel is only
// step 1 — the second step is picking which projects to subscribe to.
//
//   1. Enter PAT → validate (users/me) + fetch projects
//   2. Multi-select projects
//   3. Pick channel name + default persona
//   4. Create channel + write subscriptions + auto-restart bridge

interface AsanaProject { gid: string; name: string; workspace: { gid: string; name: string } }

export function AsanaWizard({ onCreated, onCancel, personaNames, routes, mcpByPersona }: WizardCommonProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<"pat" | "projects" | "creating" | "restarting" | "done">("pat");
  const [pat, setPat] = useState("");
  const [validation, setValidation] = useState<
    { ok: boolean; identifier?: string; details?: Record<string, unknown>; error?: string } | null
  >(null);
  const [validating, setValidating] = useState(false);
  const [projects, setProjects] = useState<AsanaProject[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [projectFilter, setProjectFilter] = useState("");
  const [name, setName] = useState("asana-1");
  const [defaults, setDefaults] = useState<ChannelDefaults>(EMPTY_CHANNEL_DEFAULTS);

  async function validatePat() {
    if (!pat) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await BridgeApi.validateChannel({
        platform: "asana",
        credentials: { personal_access_token: pat },
      });
      setValidation(res);
      if (res.ok) {
        const pj = await BridgeApi.asanaProjects(pat);
        if (pj.ok && pj.projects) setProjects(pj.projects);
        else toast.error("Project fetch failed", pj.error ?? "unknown");
        setPhase("projects");
      }
    } catch (err) {
      setValidation({ ok: false, error: String(err) });
    } finally {
      setValidating(false);
    }
  }

  function toggle(gid: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }

  async function create() {
    if (!/^[a-z0-9_-]+$/.test(name)) {
      toast.error("Invalid name", "lowercase letters, digits, _, -");
      return;
    }
    setPhase("creating");
    try {
      const ch = await BridgeApi.createChannel({
        name,
        platform: "asana",
        identifier: validation?.identifier ?? null,
        credentials: { personal_access_token: pat },
        default_persona: defaults.default_persona || null,
        default_engagement: defaults.default_engagement || null,
        default_assist_report_to: defaults.default_assist_report_to || null,
        auto_route: defaults.auto_route,
        default_mcp_servers: defaults.default_mcp_servers,
        default_instructions: defaults.default_instructions || null,
        default_acl: defaults.default_acl || null,
        status: "connected",
        enabled: true,
      });
      const items = projects
        .filter((p) => selected.has(p.gid))
        .map((p) => ({ external_id: p.gid, name: `${p.workspace.name} / ${p.name}` }));
      if (items.length > 0) {
        await BridgeApi.setSubscriptions((ch as { id: number }).id, "asana_project", items);
      }
      setPhase("restarting");
      try { await BridgeApi.restartAndWait(); }
      catch (err) { toast.error("Bridge restart timed out", String(err)); }
      setPhase("done");
      onCreated();
    } catch (err) {
      toast.error("Create failed", String(err));
      setPhase("projects");
    }
  }

  const filtered = projects.filter((p) =>
    !projectFilter ||
    p.name.toLowerCase().includes(projectFilter.toLowerCase()) ||
    p.workspace.name.toLowerCase().includes(projectFilter.toLowerCase()),
  );
  const projectsByWorkspace = filtered.reduce<Record<string, AsanaProject[]>>((acc, p) => {
    (acc[p.workspace.name] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">Add Asana account</h2>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          <XIcon className="size-3.5" />
          Cancel
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {phase === "pat" && (
          <>
            <p className="text-xs text-fg-muted">
              Generate a Personal Access Token in Asana (Profile → My Profile Settings → Apps → Create new token).
              Paste it here — the bridge validates via <code>/users/me</code> and then fetches your projects so you can pick which to subscribe to.
            </p>
            <label className="block">
              <span className="block text-xs text-fg-muted mb-1">Personal access token</span>
              <input
                value={pat}
                onChange={(e) => setPat(e.target.value.trim())}
                placeholder="2/123456..."
                className="w-full font-mono text-xs"
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <Button onClick={validatePat} loading={validating} disabled={!pat}>
              Validate & fetch projects
            </Button>
            {validation && !validation.ok && (
              <Pill tone="danger"><AlertCircle className="size-3 mr-0.5" />{validation.error}</Pill>
            )}
          </>
        )}

        {phase === "projects" && (
          <>
            <Pill tone="success"><Check className="size-3 mr-0.5" />Validated as {validation?.identifier}</Pill>

            <div>
              <h3 className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">Pick projects to subscribe</h3>
              <p className="text-[11px] text-fg-subtle mb-2">
                The bridge ingests incomplete tasks in these projects as chats; new comments become messages.
                Subscriptions can be edited later by re-editing the channel.
              </p>
              <input
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                placeholder="filter projects…"
                className="w-full text-sm mb-2"
              />
              {projects.length === 0 ? (
                <p className="text-[11px] text-fg-subtle">No projects found for this token.</p>
              ) : (
                <div className="space-y-3 max-h-[280px] overflow-y-auto panel-elevated p-2">
                  {Object.entries(projectsByWorkspace).map(([wsName, projs]) => (
                    <div key={wsName}>
                      <div className="text-[10px] uppercase tracking-wider text-fg-subtle mb-1">{wsName}</div>
                      {projs.map((p) => (
                        <label key={p.gid} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                          <input
                            type="checkbox"
                            checked={selected.has(p.gid)}
                            onChange={() => toggle(p.gid)}
                            className="size-3.5"
                          />
                          <span className="text-xs text-fg">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-fg-subtle mt-1">{selected.size} selected</p>
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <label className="block">
                <span className="block text-xs text-fg-muted mb-1">Channel name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full"
                  placeholder="asana-personal"
                />
              </label>
              <ChannelDefaultsFields
                value={defaults}
                onChange={(p) => setDefaults((d) => ({ ...d, ...p }))}
                personaNames={personaNames}
                routes={routes}
                mcpByPersona={mcpByPersona}
              />
              <Button onClick={create} disabled={!name}>Create channel</Button>
            </div>
          </>
        )}

        {phase === "creating" && <p className="text-xs text-fg-muted">Creating channel + writing subscriptions…</p>}
        {phase === "restarting" && <p className="text-xs text-fg-muted">Restarting bridge to start ingest…</p>}
        {phase === "done" && (
          <p className="text-xs text-success flex items-center gap-1">
            <Check className="size-3.5" />
            Channel is live. Backfill will run in the background.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Email ─────────────────────────────────────────────────────────────────

export function EmailWizard({ onCreated, onCancel, personaNames, routes, mcpByPersona }: WizardCommonProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<"creds" | "folders" | "creating" | "restarting" | "done">("creds");
  const [imapHost, setImapHost] = useState("imap.gmail.com");
  const [imapPort, setImapPort] = useState("993");
  const [smtpHost, setSmtpHost] = useState("smtp.gmail.com");
  const [smtpPort, setSmtpPort] = useState("465");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [from, setFrom] = useState("");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{ ok: boolean; identifier?: string; error?: string } | null>(null);
  const [folders, setFolders] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [folderFilter, setFolderFilter] = useState("");
  const [name, setName] = useState("email-1");
  const [defaults, setDefaults] = useState<ChannelDefaults>(EMPTY_CHANNEL_DEFAULTS);

  function buildCreds(): Record<string, unknown> {
    const creds: Record<string, unknown> = { imap_host: imapHost.trim(), smtp_host: smtpHost.trim(), user: user.trim(), password };
    if (imapPort.trim()) creds.imap_port = Number(imapPort);
    if (smtpPort.trim()) creds.smtp_port = Number(smtpPort);
    if (from.trim()) creds.from = from.trim();
    return creds;
  }

  async function validate() {
    if (!imapHost || !user || !password) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await BridgeApi.emailVerify(buildCreds());
      setValidation({ ok: res.ok, identifier: res.identifier, error: res.error });
      if (res.ok) {
        setFolders(res.folders ?? []);
        setPhase("folders");
      }
    } catch (err) {
      setValidation({ ok: false, error: String(err) });
    } finally {
      setValidating(false);
    }
  }

  function toggle(path: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }

  async function create() {
    if (!/^[a-z0-9_-]+$/.test(name)) {
      toast.error("Invalid name", "lowercase letters, digits, _, -");
      return;
    }
    setPhase("creating");
    try {
      const ch = await BridgeApi.createChannel({
        name,
        platform: "email",
        identifier: validation?.identifier ?? user.trim(),
        credentials: buildCreds(),
        default_persona: defaults.default_persona || null,
        default_engagement: defaults.default_engagement || null,
        default_assist_report_to: defaults.default_assist_report_to || null,
        auto_route: defaults.auto_route,
        default_mcp_servers: defaults.default_mcp_servers,
        default_instructions: defaults.default_instructions || null,
        default_acl: defaults.default_acl || null,
        status: "connected",
        enabled: true,
      });
      const items = folders.filter((f) => selected.has(f)).map((f) => ({ external_id: f, name: f }));
      if (items.length > 0) {
        await BridgeApi.setSubscriptions((ch as { id: number }).id, "email_label", items, { cascadeDisableRoutes: false });
      }
      setPhase("restarting");
      try { await BridgeApi.restartAndWait(); }
      catch (err) { toast.error("Bridge restart timed out", String(err)); }
      setPhase("done");
      onCreated();
    } catch (err) {
      toast.error("Create failed", String(err));
      setPhase("folders");
    }
  }

  const filtered = folders.filter((f) => !folderFilter || f.toLowerCase().includes(folderFilter.toLowerCase()));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">Add email account</h2>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          <XIcon className="size-3.5" />
          Cancel
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {phase === "creds" && (
          <>
            <p className="text-xs text-fg-muted">
              Inbound is IMAP polling; replies go out over SMTP, threaded into the original message.
              For Gmail, enable 2FA and create an <strong>App Password</strong> (not your account password).
              The bridge connects to verify and list your folders.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="block col-span-2 sm:col-span-1">
                <span className="block text-xs text-fg-muted mb-1">IMAP host</span>
                <input value={imapHost} onChange={(e) => setImapHost(e.target.value.trim())} placeholder="imap.gmail.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2 sm:col-span-1">
                <span className="block text-xs text-fg-muted mb-1">IMAP port</span>
                <input value={imapPort} onChange={(e) => setImapPort(e.target.value)} placeholder="993" inputMode="numeric" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2 sm:col-span-1">
                <span className="block text-xs text-fg-muted mb-1">SMTP host</span>
                <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value.trim())} placeholder="smtp.gmail.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2 sm:col-span-1">
                <span className="block text-xs text-fg-muted mb-1">SMTP port</span>
                <input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} placeholder="465" inputMode="numeric" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2">
                <span className="block text-xs text-fg-muted mb-1">Username (mailbox address)</span>
                <input value={user} onChange={(e) => setUser(e.target.value.trim())} placeholder="fleet@ayana.com" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2">
                <span className="block text-xs text-fg-muted mb-1">App password</span>
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="••••••••••••••••" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
              <label className="block col-span-2">
                <span className="block text-xs text-fg-muted mb-1">From (optional)</span>
                <input value={from} onChange={(e) => setFrom(e.target.value.trim())} placeholder='"AYANA Fleet" <fleet@ayana.com>' className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
              </label>
            </div>
            <Button onClick={validate} loading={validating} disabled={!imapHost || !user || !password}>
              Validate & list folders
            </Button>
            {validation && !validation.ok && (
              <Pill tone="danger"><AlertCircle className="size-3 mr-0.5" />{validation.error}</Pill>
            )}
          </>
        )}

        {phase === "folders" && (
          <>
            <Pill tone="success"><Check className="size-3 mr-0.5" />Connected as {validation?.identifier}</Pill>

            <div>
              <h3 className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">Filter by folder / label (optional)</h3>
              <p className="text-[11px] text-fg-subtle mb-2">
                Pick which IMAP folders (Gmail labels) to ingest. Leave all unchecked to ingest <strong>INBOX only</strong>.
                Editable later by re-editing the channel.
              </p>
              <input
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                placeholder="filter folders…"
                className="w-full text-sm mb-2"
              />
              {folders.length === 0 ? (
                <p className="text-[11px] text-fg-subtle">No folders returned.</p>
              ) : (
                <div className="space-y-0.5 max-h-[260px] overflow-y-auto panel-elevated p-2">
                  {filtered.map((f) => (
                    <label key={f} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                      <input type="checkbox" checked={selected.has(f)} onChange={() => toggle(f)} className="size-3.5" />
                      <span className="text-xs text-fg font-mono">{f}</span>
                    </label>
                  ))}
                  {filtered.length === 0 && <p className="text-[11px] text-fg-subtle">No folders match.</p>}
                </div>
              )}
              <p className="text-[11px] text-fg-subtle mt-1">{selected.size === 0 ? "INBOX only" : `${selected.size} selected`}</p>
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <label className="block">
                <span className="block text-xs text-fg-muted mb-1">Channel name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="email-fleet" />
              </label>
              <ChannelDefaultsFields
                value={defaults}
                onChange={(p) => setDefaults((d) => ({ ...d, ...p }))}
                personaNames={personaNames}
                routes={routes}
                mcpByPersona={mcpByPersona}
              />
              <Button onClick={create} disabled={!name}>Create channel</Button>
            </div>
          </>
        )}

        {phase === "creating" && <p className="text-xs text-fg-muted">Creating channel + writing folder subscriptions…</p>}
        {phase === "restarting" && <p className="text-xs text-fg-muted">Restarting bridge to start polling…</p>}
        {phase === "done" && (
          <p className="text-xs text-success flex items-center gap-1">
            <Check className="size-3.5" />
            Channel is live. The bridge is now polling for mail.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Slack (user token) ────────────────────────────────────────────────────

export function SlackWizard({ onCreated, onCancel, personaNames, routes, mcpByPersona }: WizardCommonProps) {
  const toast = useToast();
  const [phase, setPhase] = useState<"token" | "channels" | "creating" | "restarting" | "done">("token");
  const [token, setToken] = useState("");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{ ok: boolean; identifier?: string; team?: string; error?: string } | null>(null);
  const [channels, setChannels] = useState<Array<{ id: string; name: string; kind: string }>>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [chanFilter, setChanFilter] = useState("");
  const [name, setName] = useState("slack-1");
  const [defaults, setDefaults] = useState<ChannelDefaults>(EMPTY_CHANNEL_DEFAULTS);

  async function validate() {
    if (!token.trim()) return;
    setValidating(true); setValidation(null);
    try {
      const res = await BridgeApi.slackVerify(token.trim());
      setValidation({ ok: res.ok, identifier: res.identifier, team: res.team, error: res.error });
      if (res.ok) {
        const ch = await BridgeApi.slackChannels(token.trim());
        if (ch.ok && ch.channels) setChannels(ch.channels); else toast.error("Channel list failed", ch.error ?? "unknown");
        setPhase("channels");
      }
    } catch (err) { setValidation({ ok: false, error: String(err) }); }
    finally { setValidating(false); }
  }

  function toggle(id: string) {
    setSelected((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  async function create() {
    if (!/^[a-z0-9_-]+$/.test(name)) { toast.error("Invalid name", "lowercase letters, digits, _, -"); return; }
    setPhase("creating");
    try {
      const ch = await BridgeApi.createChannel({
        name, platform: "slack",
        identifier: validation?.team ?? validation?.identifier ?? null,
        credentials: { user_token: token.trim() },
        default_persona: defaults.default_persona || null,
        default_engagement: defaults.default_engagement || null,
        default_assist_report_to: defaults.default_assist_report_to || null,
        auto_route: defaults.auto_route,
        default_mcp_servers: defaults.default_mcp_servers,
        default_instructions: defaults.default_instructions || null,
        default_acl: defaults.default_acl || null,
        status: "connected", enabled: true,
      });
      const items = channels.filter((c) => selected.has(c.id)).map((c) => ({ external_id: c.id, name: c.name }));
      if (items.length > 0) await BridgeApi.setSubscriptions((ch as { id: number }).id, "slack_channel", items, { cascadeDisableRoutes: false });
      setPhase("restarting");
      try { await BridgeApi.restartAndWait(); } catch (err) { toast.error("Bridge restart timed out", String(err)); }
      setPhase("done");
      onCreated();
    } catch (err) { toast.error("Create failed", String(err)); setPhase("channels"); }
  }

  const filtered = channels.filter((c) => !chanFilter || c.name.toLowerCase().includes(chanFilter.toLowerCase()));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">Add Slack account</h2>
        <Button size="sm" variant="secondary" onClick={onCancel}><XIcon className="size-3.5" />Cancel</Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {phase === "token" && (
          <>
            <p className="text-xs text-fg-muted">
              Paste a Slack <strong>User OAuth Token</strong> (<code>xoxp-…</code>) — from your app's
              OAuth &amp; Permissions page. It acts as your account (reads/posts as you), no bot in channels.
              Inbound is polled (user tokens can't get the Events push).
            </p>
            <label className="block">
              <span className="block text-xs text-fg-muted mb-1">User OAuth Token</span>
              <input value={token} onChange={(e) => setToken(e.target.value.trim())} type="password" placeholder="xoxp-…" className="w-full font-mono text-xs" spellCheck={false} autoComplete="off" />
            </label>
            <Button onClick={validate} loading={validating} disabled={!token.trim()}>Validate &amp; list channels</Button>
            {validation && !validation.ok && <Pill tone="danger"><AlertCircle className="size-3 mr-0.5" />{validation.error}</Pill>}
          </>
        )}

        {phase === "channels" && (
          <>
            <Pill tone="success"><Check className="size-3 mr-0.5" />Connected as {validation?.identifier} · {validation?.team}</Pill>
            <div>
              <h3 className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">Channels to watch</h3>
              <p className="text-[11px] text-fg-subtle mb-2">
                Pick which channels to ingest (polled). Leave all unchecked to ingest <strong>nothing</strong> until you pick some.
                Editable later. (DMs aren't listed — would need im:read/mpim:read scopes.)
              </p>
              <input value={chanFilter} onChange={(e) => setChanFilter(e.target.value)} placeholder="filter channels…" className="w-full text-sm mb-2" />
              {channels.length === 0 ? (
                <p className="text-[11px] text-fg-subtle">No channels returned.</p>
              ) : (
                <div className="space-y-0.5 max-h-[260px] overflow-y-auto panel-elevated p-2">
                  {filtered.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-surface-2 rounded px-1">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="size-3.5" />
                      <span className="text-xs text-fg font-mono">#{c.name}</span>
                    </label>
                  ))}
                  {filtered.length === 0 && <p className="text-[11px] text-fg-subtle">No channels match.</p>}
                </div>
              )}
              <p className="text-[11px] text-fg-subtle mt-1">{selected.size} selected</p>
            </div>
            <div className="space-y-3 pt-2 border-t border-border">
              <label className="block">
                <span className="block text-xs text-fg-muted mb-1">Channel name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className="w-full" placeholder="slack-ahm" />
              </label>
              <ChannelDefaultsFields value={defaults} onChange={(p) => setDefaults((d) => ({ ...d, ...p }))} personaNames={personaNames} routes={routes} mcpByPersona={mcpByPersona} />
              <Button onClick={create} disabled={!name}>Create channel</Button>
            </div>
          </>
        )}

        {phase === "creating" && <p className="text-xs text-fg-muted">Creating channel + writing subscriptions…</p>}
        {phase === "restarting" && <p className="text-xs text-fg-muted">Restarting bridge to start polling…</p>}
        {phase === "done" && <p className="text-xs text-success flex items-center gap-1"><Check className="size-3.5" />Channel is live. The bridge is now polling Slack.</p>}
      </div>
    </div>
  );
}

// ─── Picker ──────────────────────────────────────────────────────────────

export function PlatformPicker({ onPick, onCancel }: { onPick: (p: ChannelPlatform) => void; onCancel: () => void }) {
  const options: Array<{ platform: ChannelPlatform; label: string; description: string }> = [
    { platform: "whatsapp", label: "WhatsApp", description: "Personal or business number via wacli session." },
    { platform: "telegram", label: "Telegram", description: "Bot account via BotFather token." },
    { platform: "gchat", label: "Google Chat", description: "Service account with Chat API scope." },
    { platform: "asana", label: "Asana", description: "Personal access token (Phase D — no ingest yet)." },
    { platform: "email", label: "Email", description: "IMAP polling + SMTP replies; filter by folder/label." },
    { platform: "slack", label: "Slack", description: "Your user account (xoxp token); poll channels, post as you." },
  ];
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-fg flex-1">Choose a platform</h2>
        <Button size="sm" variant="secondary" onClick={onCancel}>
          <XIcon className="size-3.5" />
          Cancel
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {options.map((o) => (
            <button
              key={o.platform}
              onClick={() => onPick(o.platform)}
              className="text-left panel-elevated p-3 hover:bg-surface-2 transition-colors"
            >
              <div className="text-sm font-medium text-fg mb-0.5">{o.label}</div>
              <div className="text-xs text-fg-muted">{o.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── New-channel webhook callout ──────────────────────────────────────────
//
// After a gchat or asana channel is created, this card surfaces the exact
// webhook URL the operator must paste into the external platform's console
// (Google Cloud Console for gchat). Includes a one-click copy + a Done
// button that fires onCreated to close the wizard.

const PLATFORM_PREFIX: Record<string, string> = {
  whatsapp: "wa", gchat: "gc", telegram: "tg", asana: "as", email: "em", slack: "sl",
};

function NewChannelWebhookCallout({
  channel,
  onClose,
}: {
  channel: { id: number; platform: string };
  onClose: () => void;
}) {
  const [publicUrl, setPublicUrl] = useState<string>("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await AuthApi.state();
        setPublicUrl(s.public_url ?? "");
      } catch { /* */ }
    })();
  }, []);

  const slug = `${PLATFORM_PREFIX[channel.platform]}-${channel.id}`;
  const url = publicUrl ? `${publicUrl.replace(/\/$/, "")}/webhook/${channel.platform}/${slug}` : "(loading…)";

  async function copy() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* */ }
  }

  const platformLabel = channel.platform === "gchat" ? "Google Chat" : "Asana";
  const consoleHint = channel.platform === "gchat"
    ? <>Paste this into the bot's HTTP endpoint in <strong>Google Cloud Console</strong> → <em>Google Chat API</em> → <em>Configuration</em>.</>
    : <>Use this when registering an Asana webhook via <code>POST /webhooks</code>. Polling-only mode keeps working without it.</>;

  return (
    <div className="panel-elevated p-3 space-y-2 border border-info/40">
      <div className="text-xs font-semibold text-fg uppercase tracking-wider">
        {platformLabel} webhook URL — paste this in the {channel.platform === "gchat" ? "Cloud Console" : "Asana webhook setup"}
      </div>
      <div className="flex items-center gap-2">
        <code className="text-xs font-mono text-fg break-all flex-1 select-all">{url}</code>
        <button
          onClick={copy}
          disabled={!publicUrl}
          className={cn(
            "inline-flex items-center gap-1 h-6 px-2 rounded text-[11px] font-medium border shrink-0",
            copied ? "bg-success-subtle text-success border-success/30" : "bg-surface-2 text-fg-muted border-border hover:text-fg",
          )}
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="text-[11px] text-fg-subtle">{consoleHint}</p>
      <div className="pt-1">
        <Button size="sm" onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}
